<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useClientStore, type Client } from '../../stores/clientStore';
import { useMatterStore, type CreatedMatter } from '../../stores/matterStore';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import { useChatStore } from '../../stores/chatStore';

const emit = defineEmits<{ (e: 'close'): void }>();

const router = useRouter();
const { t } = useI18n();
const clientStore = useClientStore();
const matterStore = useMatterStore();
const matterContextModalStore = useMatterContextModalStore();
const chatStore = useChatStore();

// Two-screen modal: 'form' shows the matter-details form (with optional
// copy-settings pane), 'success' shows the confirmation card. Submit POSTs
// to /api/matter via the matter store; on success we advance to the
// confirmation view and refresh the matter list in the background.
type Screen = 'form' | 'success';
const screen = ref<Screen>('form');
// Error from the create-matter call (e.g. duplicate name), shown in the footer.
const submitError = ref<string | null>(null);
// The persisted matter, set on a successful create; drives navigation from
// the success screen.
const createdMatter = ref<CreatedMatter | null>(null);

// Client selector state. `clientQuery` is the text in the box; `selectedClient`
// is the committed choice (an existing or freshly-created client). Editing the
// text after a selection clears it, so submit always reflects a real client.
const clientQuery = ref('');
const selectedClient = ref<Client | null>(null);
const clientResults = ref<Client[]>([]);
// True from a keystroke until that search settles (covers the debounce wait
// + the in-flight request). Drives the "Searching…" row and disables the
// "Create client" row until results are known.
const searchPending = ref(false);
const clientDropdownOpen = ref(false);
const activeIndex = ref(-1);
const clientFieldRef = ref<HTMLElement | null>(null);
// Dropdown is teleported to <body> and positioned `fixed` against the input
// so it isn't clipped by the modal body's `overflow-y: auto` / footer.
const dropdownStyle = ref<Record<string, string>>({});
let searchDebounce: ReturnType<typeof setTimeout> | null = null;
let searchSeq = 0;

const updateDropdownPosition = () => {
    const el = clientFieldRef.value;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dropdownStyle.value = {
        top: `${r.bottom + 4}px`,
        left: `${r.left}px`,
        width: `${r.width}px`,
    };
};

watch(clientDropdownOpen, (open) => {
    if (open) nextTick(updateDropdownPosition);
});

const matterName = ref('');
const matterDescription = ref('');
const copyFromExisting = ref(false);
const sourceMatterKey = ref('');
// When checked, the backend seeds the new matter database from a backup
// (pg_restore) before migrations run.
// The checkbox is forced on and disabled in the UI so every new matter is
// seeded; the specific backup is chosen via the required dropdown below.
const prepopulateTestData = ref(true);
// The chosen backup filename (from `matterStore.availableBackups`). Required:
// the form can't be submitted until one is picked. Empty string = no choice.
const selectedBackup = ref('');
// True while the backup list is being fetched on mount; drives the select's
// "Loading backups…" placeholder.
const backupsLoading = ref(false);

// Whether the query is eligible for "Create client" — non-empty and not an
// exact (case-insensitive) match of an existing result. Only meaningful once
// a search has settled; while pending, results may be stale.
const showCreateOption = computed(() => {
    const q = clientQuery.value.trim();
    if (!q) return false;
    return !clientResults.value.some((c) => c.name.toLowerCase() === q.toLowerCase());
});

// The create row is disabled while a search is pending (its eligibility isn't
// known yet); it stays visible so the option doesn't flicker in and out.
const createRowDisabled = computed(() => searchPending.value);
const showCreateRow = computed(
    () => !!clientQuery.value.trim() && (searchPending.value || showCreateOption.value),
);

// Flat list of navigable rows: each client match, then the create row only
// when it's actually actionable (shown and enabled). Index into this drives
// keyboard navigation + Enter.
type Row = { kind: 'client'; client: Client } | { kind: 'create' };
const navRows = computed<Row[]>(() => {
    const rows: Row[] = clientResults.value.map((client) => ({ kind: 'client', client }));
    if (showCreateRow.value && !createRowDisabled.value) rows.push({ kind: 'create' });
    return rows;
});

const runSearch = async (query: string) => {
    const seq = ++searchSeq;
    try {
        const results = await clientStore.searchClients(query);
        // Ignore stale responses that resolved after a newer keystroke.
        if (seq !== searchSeq) return;
        clientResults.value = results;
    } catch {
        if (seq !== searchSeq) return;
        clientResults.value = [];
    } finally {
        if (seq === searchSeq) {
            searchPending.value = false;
            activeIndex.value = navRows.value.length ? 0 : -1;
        }
    }
};

const onClientInput = () => {
    selectedClient.value = null;
    clientDropdownOpen.value = true;
    activeIndex.value = -1;
    if (searchDebounce) clearTimeout(searchDebounce);
    const query = clientQuery.value;
    if (!query.trim()) {
        searchPending.value = false;
        clientResults.value = [];
        return;
    }
    searchPending.value = true;
    searchDebounce = setTimeout(() => runSearch(query), 250);
};

const selectClient = (client: Client) => {
    selectedClient.value = client;
    clientQuery.value = client.name;
    clientDropdownOpen.value = false;
    activeIndex.value = -1;
};

const createSelectedClient = async () => {
    const name = clientQuery.value.trim();
    if (!name || clientStore.creating || createRowDisabled.value) return;
    try {
        const created = await clientStore.createClient(name);
        selectClient(created);
    } catch {
        // Surface nothing extra for the demo; store holds the error.
    }
};

const commitActiveRow = () => {
    const row = navRows.value[activeIndex.value];
    if (!row) return;
    if (row.kind === 'client') selectClient(row.client);
    else createSelectedClient();
};

const moveActive = (delta: number) => {
    const count = navRows.value.length;
    if (!count) return;
    clientDropdownOpen.value = true;
    activeIndex.value = (activeIndex.value + delta + count) % count;
};

const onClientKeydown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveActive(1);
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveActive(-1);
    } else if (e.key === 'Enter') {
        if (clientDropdownOpen.value && activeIndex.value >= 0) {
            e.preventDefault();
            commitActiveRow();
        }
    } else if (e.key === 'Escape') {
        clientDropdownOpen.value = false;
    }
};

const onClientFocus = () => {
    if (clientQuery.value.trim() && !selectedClient.value) {
        clientDropdownOpen.value = true;
    }
};

const onClientBlur = () => {
    // Delay so a mousedown on a dropdown row registers before we close.
    nextTick(() => {
        setTimeout(() => {
            clientDropdownOpen.value = false;
        }, 120);
    });
};

const onReposition = () => {
    if (clientDropdownOpen.value) updateDropdownPosition();
};

onMounted(() => {
    // capture:true so the modal body's own scroll (not just the window) keeps
    // the teleported dropdown anchored to the input.
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    // Populate the (required) backup picker. Failures leave the list empty and
    // the dropdown shows no options; the store swallows the error. While the
    // fetch is in flight the select shows a "Loading backups…" placeholder.
    backupsLoading.value = true;
    void matterStore.fetchBackups().finally(() => {
        backupsLoading.value = false;
    });
});

onBeforeUnmount(() => {
    if (searchDebounce) clearTimeout(searchDebounce);
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
});

const copyGroups = ref([
    {
        key: 'users',
        label: 'Users and permissions',
        checked: true,
        items: ['Permission groups', 'Users', 'Notifications'],
    },
    { key: 'labels', label: 'Labels', checked: true, items: ['Categories', 'Conditional rules'] },
    { key: 'ai', label: 'AI usage', checked: true, items: ['PepperGPT access'] },
]);

// Derived matter key for display only — the real generator will live
// backend-side. Picks the first three letters of the selected client's name;
// falls back to placeholder text otherwise.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generatedMatterKey = computed(() => {
    const seed = selectedClient.value?.name.trim() ?? '';
    if (!seed) return '';
    const letters = seed
        .replace(/[^A-Za-z]/g, '')
        .slice(0, 3)
        .toUpperCase();
    return letters ? `${letters}-0037` : '';
});

// Placeholder shown in the backup select's disabled first option. Doubles as
// the loading and empty-list states so they don't need a separate element.
const backupPlaceholder = computed(() => {
    if (backupsLoading.value) return t('matters.create.backupLoading');
    if (matterStore.availableBackups.length === 0) return t('matters.create.backupNone');
    return t('matters.create.backupSelect');
});

const canSubmit = computed(() => {
    if (!selectedClient.value) return false;
    if (!matterName.value.trim()) return false;
    // A backup choice is required (prepopulation is always on).
    if (!selectedBackup.value) return false;
    if (copyFromExisting.value && !sourceMatterKey.value.trim()) return false;
    return true;
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectAll = () => {
    copyGroups.value.forEach((g) => (g.checked = true));
};

const onSubmit = async () => {
    if (!canSubmit.value || matterStore.creating) return;
    submitError.value = null;
    try {
        createdMatter.value = await matterStore.createMatter({
            name: matterName.value.trim(),
            description: matterDescription.value.trim() || null,
            clientId: selectedClient.value!.id,
            prepopulate: prepopulateTestData.value,
            backupName: selectedBackup.value,
        });
        screen.value = 'success';
        // Refresh the list in the background so the new matter is present
        // when the modal closes back to the matters page.
        void matterStore.loadMatters().catch(() => {});
    } catch (err) {
        submitError.value = err instanceof Error ? err.message : t('matters.create.failed');
    }
};

const close = () => emit('close');

const onSuccessAction = () => {
    const matter = createdMatter.value;
    if (!matter) return;
    // Select the new matter up front so the route guard's ensureMatterLoaded
    // short-circuits (the background list refresh may not have landed yet).
    matterStore.selectMatter({
        RecordId: matter.id,
        Name: matter.name,
        Description: matter.description,
        ClientName: selectedClient.value?.name ?? null,
    });
    // Seed the context status as unpublished up front so the modal's
    // "setup incomplete" banner renders on the first frame instead of
    // waiting on its own /MatterContext/Status round-trip. A just-created
    // matter is definitionally unpublished; the modal's fetch still runs and
    // reconciles with the server.
    chatStore.markMatterContextUnpublished();
    // Open the matter-context modal directly: a just-created matter is known
    // to be unpublished, so don't wait for App.vue's onboarding-gate watcher
    // to confirm that via the /MatterContext/Status round-trip. The watcher
    // still runs (it hydrates chatStore.matterContextStatus for the lock
    // overlay) but sees the modal already open for this matter and bails
    // rather than re-opening.
    // auto: this is the onboarding open for a brand-new (context-less) matter,
    // so it plays the same open animation as App.vue's onboarding gate.
    void matterContextModalStore.open(matter.id, { auto: true });
    close();
    router.push({ name: 'matter', params: { matterId: matter.id } });
};
</script>

<template>
    <Teleport to="body">
        <div class="create-matter-overlay" role="dialog" aria-modal="true">
            <div class="create-matter-dialog">
                <!-- Form screen -->
                <template v-if="screen === 'form'">
                    <div class="create-matter-header">
                        <span class="create-matter-header-title">{{ t('matters.create.heading') }}</span>
                        <button class="create-matter-header-close" type="button" @click="close">
                            <span class="material-icons">close</span>
                        </button>
                    </div>

                    <div class="create-matter-body">
                        <div class="create-matter-details-grid">
                            <div>
                                <div class="create-matter-field">
                                    <div ref="clientFieldRef" class="create-matter-client-select">
                                        <div class="create-matter-input-with-icon">
                                            <input
                                                v-model="clientQuery"
                                                type="text"
                                                class="create-matter-input"
                                                :placeholder="t('matters.create.searchClients')"
                                                role="combobox"
                                                aria-autocomplete="list"
                                                :aria-expanded="clientDropdownOpen"
                                                autocomplete="off"
                                                @input="onClientInput"
                                                @keydown="onClientKeydown"
                                                @focus="onClientFocus"
                                                @blur="onClientBlur"
                                            />
                                            <span class="material-icons create-matter-input-icon"
                                                >search</span
                                            >
                                        </div>

                                        <Teleport to="body">
                                            <ul
                                                v-if="clientDropdownOpen && clientQuery.trim()"
                                                class="create-matter-client-dropdown"
                                                :style="dropdownStyle"
                                                role="listbox"
                                            >
                                                <li
                                                    v-if="searchPending"
                                                    class="create-matter-client-status"
                                                >
                                                    {{ t('matters.create.searching') }}
                                                </li>

                                                <li
                                                    v-for="(client, i) in clientResults"
                                                    :key="client.id"
                                                    class="create-matter-client-option"
                                                    :class="{
                                                        'create-matter-client-option--active':
                                                            activeIndex === i,
                                                    }"
                                                    role="option"
                                                    :aria-selected="activeIndex === i"
                                                    @mousedown.prevent="selectClient(client)"
                                                    @mousemove="activeIndex = i"
                                                >
                                                    <span
                                                        class="create-matter-client-option-name"
                                                        >{{ client.name }}</span
                                                    >
                                                </li>

                                                <li
                                                    v-if="showCreateRow"
                                                    class="create-matter-client-create"
                                                    :class="{
                                                        'create-matter-client-option--active':
                                                            activeIndex === clientResults.length &&
                                                            !createRowDisabled,
                                                        'create-matter-client-create--separated':
                                                            clientResults.length > 0,
                                                        'create-matter-client-create--disabled':
                                                            createRowDisabled,
                                                    }"
                                                    role="option"
                                                    :aria-selected="
                                                        activeIndex === clientResults.length &&
                                                        !createRowDisabled
                                                    "
                                                    :aria-disabled="createRowDisabled"
                                                    @mousedown.prevent="createSelectedClient"
                                                    @mousemove="activeIndex = clientResults.length"
                                                >
                                                    <span class="material-icons">add</span>
                                                    <span>{{
                                                        t('matters.create.createClient', {
                                                            name: clientQuery.trim(),
                                                        })
                                                    }}</span>
                                                </li>

                                                <li
                                                    v-else-if="
                                                        !searchPending && clientResults.length === 0
                                                    "
                                                    class="create-matter-client-status"
                                                >
                                                    {{ t('matters.create.noClientsFound') }}
                                                </li>
                                            </ul>
                                        </Teleport>
                                    </div>
                                </div>

                                <div class="create-matter-field">
                                    <input
                                        v-model="matterName"
                                        type="text"
                                        class="create-matter-input"
                                        :placeholder="t('matters.create.matterNamePlaceholder')"
                                    />
                                </div>

                                <!--
                            Matter key field — to be implemented in a later phase.
                        <div class="create-matter-field">
                            <label class="create-matter-field-label">Matter key</label>
                            <input
                                :value="generatedMatterKey"
                                type="text"
                                class="create-matter-input"
                                disabled
                                placeholder=""
                            />
                            <span class="create-matter-helper">
                                Matter keys are automatically set
                            </span>
                        </div>
                        -->
                            </div>

                            <div>
                                <div class="create-matter-field">
                                    <textarea
                                        v-model="matterDescription"
                                        class="create-matter-textarea"
                                        :placeholder="t('matters.create.matterDescriptionPlaceholder')"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <!--
                            Prepopulation is always on (checkbox forced checked + disabled);
                            the user chooses *which* backup to seed from via the required
                            dropdown below.
                        -->
                        <label class="create-matter-checkbox">
                            <input v-model="prepopulateTestData" type="checkbox" disabled />
                            <span class="create-matter-checkbox-label">
                                {{ t('matters.create.prepopulateLabel') }}
                            </span>
                        </label>

                        <div class="create-matter-details-grid create-matter-backup-grid">
                            <div class="create-matter-field">
                                <select
                                    v-model="selectedBackup"
                                    class="create-matter-input create-matter-select"
                                    :disabled="backupsLoading"
                                >
                                    <option value="" disabled>{{ backupPlaceholder }}</option>
                                    <option
                                        v-for="backup in matterStore.availableBackups"
                                        :key="backup"
                                        :value="backup"
                                    >
                                        {{ backup }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!--
                    Matter settings (copy-from-existing) — postponed future feature.
                    Commented out until the backend copy flow is built.
                <div class="create-matter-divider"></div>

                <div class="create-matter-settings-label">Matter settings</div>

                <div class="create-matter-settings-row">
                    <div>
                        <label class="create-matter-checkbox">
                            <input v-model="copyFromExisting" type="checkbox" />
                            <span class="create-matter-checkbox-label">
                                Copy settings from existing matter
                            </span>
                            <span class="create-matter-optional">Optional</span>
                        </label>

                        <div v-if="copyFromExisting" class="create-matter-field">
                            <label class="create-matter-field-label">Select a matter</label>
                            <div class="create-matter-input-with-icon">
                                <input
                                    v-model="sourceMatterKey"
                                    type="text"
                                    class="create-matter-input"
                                    placeholder="Matter name or key"
                                />
                                <span class="material-icons create-matter-input-icon">search</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="copyFromExisting" class="create-matter-copy-pane">
                        <div class="create-matter-copy-pane-header">
                            <button class="create-matter-select-all" @click="selectAll">
                                <span class="material-icons">done_all</span>
                                <span>Select all</span>
                            </button>
                        </div>

                        <div
                            v-for="group in copyGroups"
                            :key="group.key"
                            class="create-matter-copy-group"
                        >
                            <label class="create-matter-copy-group-header">
                                <input v-model="group.checked" type="checkbox" />
                                <span>{{ group.label }}</span>
                            </label>
                            <ul class="create-matter-copy-group-items">
                                <li v-for="item in group.items" :key="item">{{ item }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
                -->
                    </div>

                    <div class="create-matter-footer">
                        <span v-if="submitError" class="create-matter-submit-error" role="alert">
                            {{ submitError }}
                        </span>
                        <button
                            class="create-matter-secondary"
                            type="button"
                            :disabled="matterStore.creating"
                            @click="close"
                        >
                            {{ t('matters.create.cancel') }}
                        </button>
                        <button
                            class="create-matter-submit"
                            :disabled="!canSubmit || matterStore.creating"
                            @click="onSubmit"
                        >
                            <span
                                v-if="matterStore.creating"
                                class="create-matter-submit-spinner"
                                aria-hidden="true"
                            ></span>
                            {{ matterStore.creating ? t('matters.create.creating') : t('matters.create.submit') }}
                        </button>
                    </div>
                </template>

                <!-- Success screen -->
                <template v-else>
                    <div class="create-matter-success">
                        <div class="create-matter-success-check">
                            <span class="material-icons">check_circle</span>
                        </div>
                        <h2 class="create-matter-success-title">{{ t('matters.create.successTitle') }}</h2>

                        <div class="create-matter-success-card">
                            <div class="create-matter-success-field">
                                <div class="create-matter-success-field-label">{{ t('matters.create.clientLabel') }}</div>
                                <div class="create-matter-success-field-value">
                                    {{ selectedClient?.name || '—' }}
                                </div>
                            </div>

                            <div class="create-matter-success-field">
                                <div class="create-matter-success-field-label">{{ t('matters.create.matterNameLabel') }}</div>
                                <div class="create-matter-success-field-value">
                                    {{ matterName || '—' }}
                                </div>
                            </div>

                            <div class="create-matter-success-field">
                                <div class="create-matter-success-field-label">{{ t('matters.create.descriptionLabel') }}</div>
                                <div class="create-matter-success-field-value">
                                    {{ matterDescription || '—' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="create-matter-footer">
                        <button class="create-matter-secondary" type="button" @click="close">
                            {{ t('common.close') }}
                        </button>
                        <button
                            class="create-matter-submit create-matter-submit--compact"
                            type="button"
                            @click="onSuccessAction"
                        >
                            {{ t('matters.create.goToMatter') }}
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </Teleport>
</template>
